import type { LocationStatus, RejectionReasonCategory } from "@models/location";

export interface Location {
    id: string;
    name: string;
    isActive: boolean;
    // --- Admin per-location approval data (#315 / contract A) --------------
    // Enriched onto the admin GetOrganization LocationResponse. Optional so
    // pre-#315 payloads and existing fixtures still type-check (mirrors the
    // location.ts #302 `status?` precedent).
    status?: LocationStatus;
    submittedAt?: string | null;
    activatedAt?: string | null;
    reviewedAt?: string | null;
    reviewedByUserId?: string | null;
    rejectionReasonCategory?: RejectionReasonCategory | null;
    rejectionReasonNotes?: string | null;
    // E12-S5 / #240: a resale certificate was uploaded after the admin last
    // reviewed one (approved, active locations only — derived server-side).
    // Drives the "New cert" badge; POST locations/{id}/cert/acknowledge clears it.
    // Required, unlike the #315 fields above: GetOrganizationResponse's nested
    // LocationResponse always sends it (non-nullable).
    hasPendingCertReview: boolean;
    // --- Admin address edit (C6, API #433) ----------------------------------
    // On the admin GetOrganization LocationResponse only; optional for the
    // same reason as the #315 fields (this type is also the customer's
    // organization list's, which carries none of them).
    streetAddress?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    country?: string;
    // Sent back verbatim with an address edit (never through a JS Date: the
    // API compares it at 100 ns). Null: nobody has changed the location since
    // it was created.
    updatedAt?: string | null;
    // The newest certificate on file (the one the client page links to);
    // null when none is.
    newestCertificate?: LocationCertificate | null;
    // Every admin edit of the address, newest first.
    addressChanges?: LocationAddressChange[];
}

export interface LocationCertificate {
    id: string;
    fileName: string;
    uploadedAt: string;
}

export interface LocationAddress {
    streetAddress: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
}

// One admin edit of a location's address (C6): what it was, what it became,
// and the status move a change of state made (to Pending, back in review).
export interface LocationAddressChange {
    id: string;
    fromAddress: LocationAddress;
    toAddress: LocationAddress;
    fromStatus: LocationStatus;
    toStatus: LocationStatus;
    // The certificate uploaded with a change of state, downloaded at
    // GET locations/{locationId}/files/{fileId}; null when none was.
    certificateFileId: string | null;
    changedAt: string;
    changedBy: string;
    // The admin's "First Last".
    changedByUserName: string;
}

export interface OrganizationRole {
    userId: string;
    userName: string;
    userEmail: string;
    role: number;
}

export enum OrganizationUserRole {
    None = 0,
    Owner = 1,
    Member = 2,
}

export const ORGANIZATION_USER_ROLE_LABEL: Record<number, string> = {
    [OrganizationUserRole.None]: "—",
    [OrganizationUserRole.Owner]: "Owner",
    [OrganizationUserRole.Member]: "Member",
};

// --- Org-level membership (#352 / #313) ----------------------------------
// There is exactly ONE subscription per organization (post-#312), so these
// types live on the org model. The per-location subscription surface
// (LocationResponse.subscription and the /locations/{id}/subscriptions verbs)
// was removed with this change.

export enum SubscriptionStatus {
    None = 0,
    // 1 was Trial — never granted and never billed; retired by the API (#346).
    // parseSubscriptionStatus reads a stray 1 as None.
    Active = 2,
    Expired = 3,
    Cancelled = 4,
    Suspended = 5,
}

export const SUBSCRIPTION_STATUS_LABEL: Record<SubscriptionStatus, string> = {
    [SubscriptionStatus.None]: "—",
    [SubscriptionStatus.Active]: "Active",
    [SubscriptionStatus.Expired]: "Expired",
    [SubscriptionStatus.Cancelled]: "Cancelled",
    [SubscriptionStatus.Suspended]: "Suspended",
};

// #376 / Team B contract (`OrganizationPaymentMethodResponse`). Display fields
// ONLY — the API exposes no vendor identifier here (no VendorPaymentProfileId, no
// OriginalNetworkTransId) and none may be added: nothing in the browser has any
// use for one, and a stored-card handle is not something to put in a bundle.
export interface OrganizationPaymentMethod {
    cardBrand: string | null;
    cardLast4: string | null;
}

export interface OrganizationSubscription {
    id: string;
    subscriptionPlanId: number;
    subscriptionPlanName: string;
    status: SubscriptionStatus;
    autoRenew: boolean;
    currentPeriodEnd: string;
    cancelAtPeriodEnd: boolean;
}

export const parseSubscriptionStatus = (
    value: number | string | null | undefined,
): SubscriptionStatus => {
    if (typeof value === "number") {
        return value in SubscriptionStatus ? (value as SubscriptionStatus) : SubscriptionStatus.None;
    }

    if (typeof value === "string") {
        const numeric = Number(value);
        if (!Number.isNaN(numeric) && numeric in SubscriptionStatus) {
            return numeric as SubscriptionStatus;
        }

        const match = (
            Object.keys(SubscriptionStatus) as Array<keyof typeof SubscriptionStatus>
        ).find((key) => key.toLowerCase() === value.trim().toLowerCase());

        if (match) {
            return SubscriptionStatus[match];
        }
    }

    return SubscriptionStatus.None;
};

export interface OrganizationResponse {
    id: string;
    name: string;
    streetAddress: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
    email: string;
    firstName: string;
    lastName: string;
    phoneNumber: string;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
    createdBy: string;
    updatedBy: string;
    locations: Location[];
    roles: OrganizationRole[];
    // Nullable: an org that has never been billed has no subscription yet.
    subscription?: OrganizationSubscription | null;
    // Org-level, NOT nested under `subscription` (#376, Lead ruling 2026-08-06).
    // CreatePaymentMethodCommand persists the card BEFORE activation runs, and
    // SubscriptionActivationService only creates the Subscription row once the
    // activation charge clears — so an organization can hold a saved card with no
    // subscription row at all, which is exactly what every 402 leaves behind
    // (payment_declined / payment_unconfirmed / activation_unavailable). Nested
    // under the subscription the card would be unreachable in precisely the state
    // the membership tab exists to handle. It is an org-level fact in the schema
    // too: PaymentProfile.OrganizationId, with an org-level filtered unique index.
    // `undefined` must be read exactly like `null`.
    paymentMethod?: OrganizationPaymentMethod | null;
}

// PUT organizations/{id} (C5, API #426): a platform admin edits a client. The
// tax id is not editable. `updatedAt` is the detail read's own string, sent
// back untouched: the API compares it to the stored instant at 100 ns, so a
// copy that went through a JS Date (milliseconds) reads as stale and is
// refused with organization_changed. Null is a real value: a client nobody
// has edited since it was created.
export interface UpdateOrganizationRequest {
    name: string;
    legalBusinessName: string;
    streetAddress: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
    updatedAt: string | null;
}

// `invitationSent`: null when the edit issued no new signup link; otherwise
// whether the new link's email to the changed contact email went out (a
// failed send keeps the edit, and the admin can resend the link).
export interface UpdateOrganizationResponse {
    updatedAt: string | null;
    invitationSent: boolean | null;
}

// POST organizations (C3, API PR 419): a platform admin creates a client, its
// first location (approved in the same step by the certificate the admin
// uploads) and the contact's signup invitation. Sent as multipart.
// `confirmDuplicateTaxId`: the admin saw the duplicate tax id warning
// (tax_id_in_use) and creates the client anyway.
export interface CreateClientRequest {
    name: string;
    legalBusinessName: string;
    taxId: string;
    streetAddress: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
    locationName: string;
    locationStreetAddress: string;
    locationCity: string;
    locationState: string;
    locationZipCode: string;
    locationCountry: string;
    resaleCertificate: File;
    confirmDuplicateTaxId: boolean;
}

// The first location's approval, in ApproveOrganization's per-location shape.
// A new client has no card, so an approved location is Approved with no charge
// and waits for the card its contact adds after signing up (C3/C4 follow-up).
// `approved` false, with `error`, when the approval failed after the client
// was created (it stays Pending) or another admin reviewed it at that moment.
export interface CreateClientLocationResult {
    locationId: string;
    approved: boolean;
    error: string | null;
    activationOutcome: LocationActivationOutcome;
    status: LocationStatus;
}

// The 201. `invitationSent` is false when the signup email could not be sent:
// the client and its link are saved regardless, and the admin can resend it.
export interface CreateClientResponse {
    organizationId: string;
    locationId: string;
    invitationEmail: string;
    invitationExpiresAt: string;
    invitationSent: boolean;
    location: CreateClientLocationResult;
}

// --- Admin clients list (E5-S7 / US 194) ---------------------------------
// Enum integer values mirror the backend (Swagger exposes them without names):
//   OrganizationStatusType, GetOrganizationsSearchOptionType,
//   GetOrganizationsSortOptionType, plus the shared SearchType / sort direction.

// There is NO organization approval — approval lives only on Location
// (DecisionLog §1.2, backend #326). "Active" is the absence of a status, so
// None is the normal state; Suspended (4) keeps its stored value from before
// Pending/Approved/Rejected were dropped, and Cancelled continues the sequence.
export enum OrganizationStatus {
    None = 0,
    Suspended = 4,
    Cancelled = 5,
}

export enum GetOrganizationsSearchOptionType {
    None = 0,
    Name = 1,
    LegalBusinessName = 2,
    TaxId = 3,
}

export enum GetOrganizationsSortOptionType {
    None = 0,
    Id = 1,
    Name = 2,
    LegalBusinessName = 3,
    Status = 4,
    SubmittedAt = 5,
    // 6 was ReviewedAt — dropped with the org-level review fields (#327).
    CreatedAt = 7,
    // #333: the active Pending locations the Awaiting Approval column shows.
    PendingLocationsCount = 8,
}

export enum OrganizationSearchType {
    None = 0,
    Contains = 1,
    Equals = 2,
    StartsWith = 3,
    EndsWith = 4,
}

export enum OrganizationSortDirection {
    None = 0,
    Asc = 1,
    Desc = 2,
}

export interface OrganizationListItem {
    id: string;
    name: string;
    legalBusinessName: string;
    // Nullable on the API (GetOrganizationsResponse.TaxId is string?).
    taxId: string | null;
    status: OrganizationStatus;
    submittedAt: string;
    locationsCount: number;
    // Count of the org's active locations with Status == Pending (#315 /
    // contract A) — drives the admin list's "Awaiting approval" column.
    pendingLocationsCount: number;
    // #434: active locations waiting for payment (passed review, activation
    // charge not through yet). Its own column, so a client whose only open
    // locations are waiting for payment is findable from the list.
    pendingPaymentLocationsCount: number;
    // E12-S5 / #240: true when ANY active, approved location of this client
    // has a certificate uploaded after its last review — the list's "New cert"
    // badge. The detail page says which location.
    hasPendingCertReview: boolean;
    // B3 (API #428): the latest review (approve or reject) across the client's
    // active locations, ISO UTC; null when none has been reviewed.
    lastReviewedAt: string | null;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
}

// #428: the container is private, so the API sends no blob URL — the bytes
// come from GET /organizations/{organizationId}/files/{id}.
export interface OrganizationFile {
    id: string;
    fileName: string;
}

export interface OrganizationDetail {
    id: string;
    name: string;
    // Nullable on the API (GetOrganizationResponse.LegalBusinessName is string?).
    legalBusinessName: string | null;
    // Nullable on the API (GetOrganizationResponse.TaxId is string?).
    taxId: string | null;
    streetAddress: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
    email: string;
    firstName: string;
    lastName: string;
    phoneNumber: string;
    status: OrganizationStatus;
    isActive: boolean;
    // #438: the applicant has not completed signup yet, so the client detail
    // offers "Resend invitation". Always sent by GetOrganizationResponse.
    isAwaitingSignup: boolean;
    // PR 327 round 6: the server's UTC time it read this detail, stamped
    // before the read. The approve sends it back, untouched, as
    // certificatesSeenAt. Optional: an older API omits it, and the approve
    // then sends none.
    retrievedAt?: string;
    submittedAt: string | null;
    createdAt: string;
    updatedAt: string | null;
    createdBy: string;
    updatedBy: string | null;
    locations: Location[];
    roles: OrganizationRole[];
    files: OrganizationFile[];
    // Newest first, always present (empty when the status never changed).
    statusHistory: OrganizationStatusHistoryEntry[];
}

// #460: one row per status change, written by PUT organizations/{id}/status.
export interface OrganizationStatusHistoryEntry {
    id: string;
    fromStatus: OrganizationStatus;
    toStatus: OrganizationStatus;
    changedAt: string;
    // A user id only — the response carries no name.
    changedByUserId: string | null;
    // Stored trimmed; null when none was given (and on rows from before AO-09).
    reason: string | null;
}

// Reactivate sends status: None ("active" is the absence of a status).
// AO-09 (#460): `reason` is required (non-blank) when cancelling and optional
// otherwise; the API caps it at 500 characters after trimming. Leave it
// undefined to send none — JSON drops it from the body.
export interface UpdateOrganizationStatusRequest {
    status: OrganizationStatus;
    reason?: string;
}

// --- Admin approve action (E5-S9 / #314, #434) ---------------------------
// POST organizations/{id}/approve processes each Pending and PendingPayment
// location. A client with no card yet is approved with no charge (§1.6). A
// client that already pays is charged each location's prorated first month
// FIRST, and only a charge that goes through approves it; any other outcome
// leaves the location PendingPayment. Partial failure is still 200, so each
// result's `status` + `activationOutcome` is what tells the outcomes apart, and
// `approved` is true only when `status` is Approved. Re-POSTing skips
// locations already approved, so it never charges one twice.

// Mirrors the backend LocationActivationOutcomeType (an integer on the wire).
export enum LocationActivationOutcome {
    None = 0,
    // No charge: the client has no card / paid-up membership yet, or an
    // earlier location in the same approval did not activate.
    NotAttempted = 1,
    Activated = 2,
    Declined = 3,
    // Outcome unknown or held for review — money MAY have moved.
    Unconfirmed = 4,
    // Refused on our side before the gateway was called.
    Unavailable = 5,
}

export interface LocationApprovalResult {
    locationId: string;
    approved: boolean;
    error: string | null;
    // The location's status after this approval: Approved or PendingPayment,
    // or still Pending when it has no resale certificate and was skipped
    // (#437) — the one case `error` is set, naming the location.
    status: LocationStatus;
    activationOutcome: LocationActivationOutcome;
}

export interface ApproveOrganizationResponse {
    results: LocationApprovalResult[];
}

// Request of POST organizations/{organizationId}/approved-locations (C4, API
// #427): an admin adds a location to a client and approves it in the same
// step, with its business certificate. The 201 is a LocationApprovalResult.
export interface CreateApprovedLocationRequest {
    name: string;
    streetAddress: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
    resaleCertificate: File;
}

// Response of POST organizations/{id}/invitation/resend (#438): where the new
// signup link was sent, and until when it works (ISO date).
export interface ResendOrganizationInvitationResponse {
    email: string;
    expiresAt: string;
}

export interface PaginatedOrganizations {
    pageNumber: number;
    totalPages: number;
    // Rows matching the filter.
    count: number;
    // The rows before any filter (PO ruling X4, API #425): every client the
    // list can show, before the chips and the search. Sent on admin reads
    // only; while absent the total reads "{count} results".
    allCount?: number;
    // X2 (API #428): clients with at least one pending location, before the
    // chips and the search. Drives the pending banner; absent where allCount is.
    pendingCount?: number;
    hasPreviousPage: boolean;
    hasNextPage: boolean;
    items: OrganizationListItem[];
}

// Response of GET /organizations/statuses — global per-LOCATION status counts
// (approval is per-location, DecisionLog §1.2; endpoint repurposed by #326,
// naming aligned by #332). Drives the admin top-bar badge; independent of the
// list query.
export interface LocationStatusCounts {
    total: number;
    pending: number;
    approved: number;
    rejected: number;
    suspended: number;
    // #434: passed review, waiting for the activation charge.
    pendingPayment: number;
}

export interface GetOrganizationsQuery {
    pageNumber?: number;
    pageSize?: number;
    search?: string;
    searchType?: OrganizationSearchType;
    searchBy?: GetOrganizationsSearchOptionType[];
    sortBy?: GetOrganizationsSortOptionType[];
    sortDirection?: OrganizationSortDirection;
    // Omit (leave undefined) for "All"; None=0 is never sent as a filter.
    status?: OrganizationStatus;
    // C1 (API #428): clients with at least one active location in this
    // status. The API accepts Pending, Approved and Rejected only, and ANDs
    // it with `status`; the list's chips send one or the other, never both.
    locationStatus?: LocationStatus;
}

export const DEFAULT_ORGANIZATIONS_QUERY: GetOrganizationsQuery = {
    pageNumber: 1,
    pageSize: 25,
    search: "",
    searchType: OrganizationSearchType.Contains,
    searchBy: [
        GetOrganizationsSearchOptionType.Name,
        GetOrganizationsSearchOptionType.TaxId,
    ],
    sortBy: [GetOrganizationsSortOptionType.SubmittedAt],
    sortDirection: OrganizationSortDirection.Desc,
};
